import type { AskAiContactResult, AskAiBusinessResult } from '~/composables/useAiSearch'

export type AskAiDirection = 'contacts' | 'businesses'

export type AskAiContactCardData = {
  kind: 'contact'
  contactId: string
  name: string
  similarity: number
  reason: string | null
}

export type AskAiBusinessCardData = {
  kind: 'business'
  businessId: string
  name: string
  similarity: number
  reason: string | null
}

export type AskAiCard = AskAiContactCardData | AskAiBusinessCardData

export type AskAiMessage =
  | { role: 'user'; kind?: 'picker'; text: string; createdAt: string }
  | { role: 'ai'; status: 'greeting'; createdAt: string }
  | { role: 'ai'; status: 'typing'; createdAt: string }
  | { role: 'ai'; status: 'searching' | 'empty' | 'error'; text: string; createdAt: string }
  | {
      role: 'ai'
      status: 'ambiguous'
      introText: string
      suggestedDefault: AskAiDirection
      createdAt: string
    }
  | {
      role: 'ai'
      status: 'results'
      introText: string
      direction: AskAiDirection
      cards: AskAiCard[]
      createdAt: string
    }

export type AskAiThread = {
  workspaceId: string
  direction: AskAiDirection | null
  messages: AskAiMessage[]
  updatedAt: string
}

const STORAGE_PREFIX = 'askAi:thread:'

function storageKey(workspaceId: string) {
  return `${STORAGE_PREFIX}${workspaceId}`
}

function emptyThread(workspaceId: string): AskAiThread {
  return { workspaceId, direction: null, messages: [], updatedAt: new Date().toISOString() }
}

function readThread(workspaceId: string): AskAiThread {
  if (typeof window === 'undefined') {
    return emptyThread(workspaceId)
  }
  try {
    const raw = window.localStorage.getItem(storageKey(workspaceId))
    if (!raw) {
      return emptyThread(workspaceId)
    }
    const parsed = JSON.parse(raw) as Partial<AskAiThread>
    if (parsed.workspaceId !== workspaceId) {
      return emptyThread(workspaceId)
    }
    return {
      workspaceId,
      direction: parsed.direction ?? null,
      messages: parsed.messages ?? [],
      updatedAt: parsed.updatedAt ?? new Date().toISOString(),
    }
  } catch {
    return emptyThread(workspaceId)
  }
}

function writeThread(thread: AskAiThread) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(storageKey(thread.workspaceId), JSON.stringify(thread))
}

function clearStoredThread(workspaceId: string) {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(storageKey(workspaceId))
}

export function contactResultsToCards(results: AskAiContactResult[]): AskAiContactCardData[] {
  return results.map((r) => ({
    kind: 'contact',
    contactId: r.id,
    name: r.name,
    similarity: r.similarity,
    reason: r.reason ?? null,
  }))
}

export function businessResultsToCards(results: AskAiBusinessResult[]): AskAiBusinessCardData[] {
  return results.map((r) => ({
    kind: 'business',
    businessId: r.id,
    name: r.name,
    similarity: r.similarity,
    reason: r.reason ?? null,
  }))
}

export function useAskAiThread() {
  const workspaceCookie = useCookie<string | null>('c-wId')
  const messages = ref<AskAiMessage[]>([])
  const direction = ref<AskAiDirection | null>(null)

  const load = () => {
    const wsId = workspaceCookie.value
    if (!wsId) {
      messages.value = []
      direction.value = null
      return
    }
    const thread = readThread(wsId)
    const last = thread.messages[thread.messages.length - 1]
    const sanitized = last && last.role === 'ai' && last.status === 'typing'
      ? thread.messages.slice(0, -1)
      : thread.messages
    messages.value = sanitized
    direction.value = thread.direction
  }

  const persist = () => {
    const wsId = workspaceCookie.value
    if (!wsId) return
    writeThread({
      workspaceId: wsId,
      direction: direction.value,
      messages: messages.value,
      updatedAt: new Date().toISOString(),
    })
  }

  const append = (msg: AskAiMessage) => {
    messages.value = [...messages.value, msg]
    persist()
  }

  const replaceLast = (msg: AskAiMessage) => {
    if (messages.value.length === 0) {
      append(msg)
      return
    }
    messages.value = [...messages.value.slice(0, -1), msg]
    persist()
  }

  const setDirection = (d: AskAiDirection | null) => {
    direction.value = d
    persist()
  }

  const clear = () => {
    messages.value = []
    direction.value = null
    const wsId = workspaceCookie.value
    if (wsId) clearStoredThread(wsId)
  }

  watch(
    () => workspaceCookie.value,
    () => {
      load()
    },
  )

  const concatenatedUserText = computed(() =>
    messages.value
      .filter((m): m is Extract<AskAiMessage, { role: 'user' }> => m.role === 'user' && m.kind !== 'picker')
      .map((m) => m.text)
      .join('\n'),
  )

  return {
    messages,
    direction,
    load,
    append,
    replaceLast,
    setDirection,
    clear,
    concatenatedUserText,
  }
}
